import React, { PropTypes, PureComponent } from 'react';

import { withAsyncScript } from '../AsyncScriptWrapper/AsyncScriptWrapper.react';

/**
 * Geopicker component
 * Provides an input checkbox to requeset the navigator geolocation, stored in state.
 * Calls method on change consistent with other form inputs.
 */
class Geopicker extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      fetching: false,
      location: null,
    };
  }

  getPosition = () => {
    this.setState({
      fetching: true,
    });
    const { maximumAge, onFetching, timeout } = this.props;
    onFetching({
      target: { name: this.input.name },
      type: 'fetching',
    });
    navigator.geolocation.getCurrentPosition(this.handlePosition, this.handlePositionError,
      { timeout, maximumAge },
    );
    // Timeout doens't guarantee 'error', nor do some brower rejections
    // Manually check our location state after a later timeout and force the error if empty
    setTimeout(() => {
      if (this.state.location === null) {
        this.handlePositionError(); // Timeout
      }
    }, this.props.timeout + 1000); // Wait extra second
  }

  handleChange = (event) => {
    if (navigator && 'geolocation' in navigator) {
      if (event.target.checked) {
        this.getPosition();
      } else {
        // Reset location to allow refetch
        this.setState({
          location: null,
        });
      }
    }
  }

  handlePosition = (position) => {
    const { latitude: lat, longitude: lng } = position.coords;
    const { googleAPI, onFetched } = this.props;
    const geocoder = new googleAPI.maps.Geocoder();
    // Use Google Geocoder to resolve coords to Google place
    geocoder.geocode({ location: { lat, lng } }, ((results, status) => {
      let location = null;
      if (status === 'OK') {
        const firstResult = results[0];
        const localityName = firstResult.address_components.filter(a => a.types.includes('locality'))[0].short_name || 'Current location';
        location = { ...firstResult, name: localityName };
      } else {
        // Otherwise mock Place shape
        location = {
          name: 'Current location',
          geometry: {
            location: {
              lat: () => lat,
              lng: () => lng,
            },
          },
          types: ['geolocation'],
        };
      }
      onFetched({
        target: { name: this.input.name },
        type: 'fetched',
      });
      this.setState({
        fetching: false,
        location,
      }, () => {
        this.props.onChange({
          target: { name: this.input.name, value: this.state.location },
          type: 'change',
        });
      });
    }));
  }

  // Error: geolocation denied, unavailable or timed out
  handlePositionError = () => {
    // Check that the component hasn't unmounted
    if (this.input) {
      const { onFetched } = this.props;
      onFetched({
        target: { name: this.input.name },
        type: 'fetched',
      });
      this.setState({
        fetching: false,
      });
    }
  }

  render() {
    const {
      className,
      disabled,
      fetchingLabel,
      id,
      label,
      maximumAge,
      timeout,
      googleAPI,
      onFetched,
      onFetching,
      ...props
    } = this.props;
    const {
      fetching,
    } = this.state;
    return (
      <label
        htmlFor={id}
        className={className}
        style={{ opacity: fetching || disabled ? 0.5 : 1 }}
      >
        <input
          id={id}
          type="checkbox"
          {...props}
          disabled={fetching || disabled}
          onChange={this.handleChange}
          ref={(node) => { this.input = node; }}
        /> {fetching ? fetchingLabel : label}
      </label>
    );
  }
}

Geopicker.propTypes = {
  /**
   * String class names to append to component styles.
   */
  className: PropTypes.string,
  /**
   * Disable state of input (and component)
   */
  disabled: PropTypes.bool,
  /**
   * Text label for input while fetching
   */
  fetchingLabel: PropTypes.string,
  /**
   * Google Maps API object, supplied by async script wrapper component (for client only)
   */
  googleAPI: PropTypes.shape(),
  /**
   * String value for label and input
   */
  id: PropTypes.string.isRequired,
  /**
   * Text label for input
   */
  label: PropTypes.string,
  /**
   * Milliseconds to cache position
   */
  maximumAge: PropTypes.number,
  /**
   * Function called on place change or select
   */
  onChange: PropTypes.func,
  /**
   * Function called on fetching geolocation
   */
  onFetched: PropTypes.func,
  /**
   * Function called on geolocation response or error
   */
  onFetching: PropTypes.func,
  /**
   * Milliseconds to timeout geolocation request
   */
  timeout: PropTypes.number,
};

Geopicker.defaultProps = {
  className: '',
  disabled: false,
  fetchingLabel: 'Fetching your location',
  googleAPI: undefined,
  label: 'Use my location',
  onChange: () => {},
  onFetched: () => {},
  onFetching: () => {},
  maximumAge: 1000 * 60 * 5,
  timeout: 10000,
};

// Configuration of async script requirement
const asyncScriptProps = {
  // ID given to script tag in index
  id: 'gmaps',
  // Global variable name when ready
  global: 'google',
  // Name to deliver global as prop (ensure loaded scope)
  propName: 'googleAPI',
};

// Wrap with async script loader, which adds global object as prop when ready
const GeopickerWithAsync = withAsyncScript(Geopicker, asyncScriptProps);

export default GeopickerWithAsync;

// Export unwrapped class for tests
export const GeopickerClass = Geopicker;



// WEBPACK FOOTER //
// ./app/src/components/Geopicker/Geopicker.react.js